import { useOriginNavigate } from '@/lib/readiness/nav'
import type { DomainDef, IndexDef, ObjectiveDef, ObjectiveState } from '@/api/types/readiness'
import { transitionFor } from '@/api/types/readiness'
import { levelName, sectorBenchmark, tierForScore, tiersOf } from '@/lib/indexes/registry'
import { liveLevel, liveScore, needsScoreConfirmation } from '@/lib/readiness/score'
import { assertScale, fmtScore } from '@/lib/readiness/scale'
import { Chip, Icon, ProvenanceChips, ScoreBar, Stepper } from '@/components/readiness/primitives'
import { Term } from '@/components/readiness/Term'
import { formatDate } from '@/components/common/format'

// Objective card (§6.3): 3px domain-coloured left edge, two-column grid that
// stacks at narrow widths (stepper moves below the text). Two renderers keyed
// on index.scale (I2 Note 6): levels → stepper; score_0_10 → ten-segment bar.
export function ObjectiveCard({ index, domain, objective, state, evidenceCount, evidenceSources, plansInProgress, color, contextFigure }: { index: IndexDef; domain: DomainDef; objective: ObjectiveDef; state: ObjectiveState | undefined; evidenceCount: number; evidenceSources: string[]; plansInProgress: number; color: string; contextFigure?: string | null }) {
  const navigate = useOriginNavigate()
  const scale = assertScale(index)
  const href = `/readiness/${objective.code}?index=${index.id}`
  const isUnset = !state || state.status === 'unset'
  const level = liveLevel(state)
  const score = liveScore(state)
  const transition = transitionFor(level)
  const first = scale === 'levels' ? (objective.recommendations[transition]?.[0] ?? '') : (objective.actions?.[0]?.text ?? '')
  const firstRef = scale === 'levels' ? null : (objective.actions?.[0]?.source_page ?? null)
  const nextLabel = scale === 'levels' ? (level === 'L3' ? 'Sustain:' : `To reach L${Number(level[1]) + 1}:`) : 'Next:'
  const tier = scale === 'score_0_10' ? tierForScore(index, objective.code, score) : null
  const sector = scale === 'score_0_10' ? sectorBenchmark(index, objective.code) : null
  const ariaValue = scale === 'levels' ? `${level} ${levelName(index, level)}` : isUnset ? 'not scored' : fmtScore(score)
  return (
    <article
      className="rd-card objective-card relative cursor-pointer p-5 transition-colors sm:px-[22px]"
      style={{ borderLeft: `3px solid ${color}` }}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('a,button')) return
        navigate(href, e.currentTarget)
      }}
      data-origin-key={href}
      tabIndex={-1}
      data-testid={`objective-card-${objective.code}`}
      aria-label={`${objective.code} · ${objective.title} · ${ariaValue}`}
    >
      <div className="grid gap-4 sm:gap-6 md:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <span className="rd-mono text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{objective.code}</span>
            {objective.no_regrets && (
              <Term index={index} term="No-regrets" className="!no-underline">
                <Chip tone="chip">NO-REGRETS</Chip>
              </Term>
            )}
          </div>
          <h3 className="text-[18px] font-bold leading-snug">
            <a href={href} onClick={(e) => { e.preventDefault(); navigate(href, e.currentTarget.closest('article')) }} className="hover:underline">{objective.title}</a>
          </h3>
          <div className="mt-2">
            {scale === 'levels' ? (
              <ProvenanceChips index={index} state={state} evidenceCount={evidenceCount} plansInProgress={plansInProgress} sources={evidenceSources} />
            ) : (
              <div className="flex flex-wrap items-center gap-1.5">
                {isUnset ? (
                  <Chip tone="dashed">Not yet scored</Chip>
                ) : state?.status === 'suggested' ? (
                  <Chip tone="warn" title={state.suggested_reason ?? undefined}><Icon name="info" size={12} /> Suggested {fmtScore(state.suggested_score)} · needs confirmation</Chip>
                ) : (
                  <>
                    <Chip tone="tile">{state?.status === 'confirmed' ? 'Confirmed' : 'Scored'} by {state?.set_by} · {formatDate(state?.set_at)}</Chip>
                    {needsScoreConfirmation(state) && <Chip tone="warn" title={state?.suggested_reason ?? undefined}><Icon name="info" size={12} /> System now suggests {fmtScore(state?.suggested_score)}</Chip>}
                  </>
                )}
                {sector != null && score != null && score < sector && <Chip tone="neutral">Below sector average</Chip>}
                <Chip tone="tile">{evidenceCount} evidence</Chip>
                {plansInProgress > 0 && <Chip tone="info">{plansInProgress} {plansInProgress === 1 ? 'plan' : 'plans'} in progress</Chip>}
              </div>
            )}
          </div>
          {first && (
            <p className="mt-3 text-[14px] leading-relaxed" style={{ color: 'var(--rd-text-muted)' }}>
              <span className="font-medium" style={{ color: 'var(--rd-text-secondary)' }}>{nextLabel}</span> {first}
              {contextFigure ? <span> {contextFigure}</span> : null}
              {firstRef ? <span style={{ color: 'var(--rd-text-faint)' }}> ({firstRef})</span> : null}
            </p>
          )}
        </div>
        <div className="flex flex-col justify-center">
          {scale === 'levels' ? (
            <>
              <p className="rd-label">Current level</p>
              <p className="mb-2 text-[18px] font-bold leading-tight" style={{ color: level === 'L0' ? 'var(--rd-text-muted)' : color }}>
                {level} {levelName(index, level)}
              </p>
              <Stepper level={level} color={color} status={isUnset ? 'unset' : state?.status} />
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="rd-label">Score</p>
                {tier && !isUnset && <Chip tone="tile" style={{ fontWeight: 700, letterSpacing: '0.06em' }}>TIER {tier.n} OF {tiersOf(index, objective.code).length}</Chip>}
              </div>
              <p className="rd-mono mb-1 text-[28px] font-extrabold leading-tight" style={{ color: isUnset ? 'var(--rd-text-muted)' : color }}>{isUnset ? '—' : fmtScore(score)}<span className="text-[12.5px] font-normal" style={{ color: 'var(--rd-text-faint)' }}> / 10</span></p>
              <ScoreBar score={score} color={color} sector={sector} suggested={state?.status === 'suggested'} />
            </>
          )}
        </div>
      </div>
      <span className="sr-only">{domain.name}</span>
    </article>
  )
}
